import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Search, 
  Folder,
  FolderOpen,
  ArrowLeft,
  File,
  FileImage,
  FileSpreadsheet,
  Scale,
  Megaphone,
  Briefcase,
  Settings,
  Loader2,
  UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allFolders = [
  { id: 'juridique', label: 'Juridique', icon: Scale, color: 'text-blue-600', bgColor: 'bg-blue-50', roles: ['sa', 'soa', 'admin', 'rh', 'commercial'] },
  { id: 'commercial', label: 'Commercial', icon: Briefcase, color: 'text-emerald-600', bgColor: 'bg-emerald-50', roles: ['sa', 'soa', 'admin', 'rh', 'commercial'] },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, color: 'text-purple-600', bgColor: 'bg-purple-50', roles: ['sa', 'soa', 'admin', 'rh', 'commercial'] },
  { id: 'operationnel', label: 'Operationnel', icon: Settings, color: 'text-amber-600', bgColor: 'bg-amber-50', roles: ['sa', 'soa', 'admin', 'rh', 'commercial'] },
  { id: 'rh', label: 'RH', icon: UserCheck, color: 'text-teal-600', bgColor: 'bg-teal-50', roles: ['sa', 'soa', 'admin', 'rh'] },
];

const getFileIcon = (fileType) => {
  const type = fileType?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) {
    return { icon: FileImage, color: 'text-pink-500' };
  }
  if (['xls', 'xlsx', 'csv'].includes(type)) {
    return { icon: FileSpreadsheet, color: 'text-emerald-600' };
  }
  if (['pdf'].includes(type)) {
    return { icon: FileText, color: 'text-red-500' };
  }
  if (['doc', 'docx'].includes(type)) {
    return { icon: FileText, color: 'text-blue-600' };
  }
  return { icon: File, color: 'text-slate-500' };
};

export const Documents = () => {
  const { role } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Filter folders based on user role
  const folders = allFolders.filter(folder => folder.roles.includes(role));

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API}/documents`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const [downloading, setDownloading] = useState(null);
  
  const downloadDocument = async (documentId, name, fileType) => {
    setDownloading(documentId);
    try {
      const response = await fetch(`${API}/documents/${documentId}/download`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.${fileType}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  // Get documents for a folder - supports multi-category documents
  const getDocumentsInFolder = (folderId) => {
    return documents.filter(doc => {
      // Check if document has categories array or single category
      const docCategories = doc.categories || [doc.category];
      return docCategories.includes(folderId);
    });
  };

  // Filter documents in current folder by search
  const filteredDocuments = currentFolder 
    ? getDocumentsInFolder(currentFolder).filter(doc => 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const getFolderCount = (folderId) => getDocumentsInFolder(folderId).length;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border border-slate-200 overflow-hidden">
      {/* Toolbar - Windows Explorer style */}
      <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200">
        {currentFolder && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setCurrentFolder(null); setSearchQuery(''); setSelectedDoc(null); }}
            className="h-8 px-2 gap-1"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
        )}
        
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <Folder className="w-4 h-4 text-amber-500" />
          <span className="font-medium text-slate-700">Documents</span>
          {currentFolder && (
            <>
              <span className="text-slate-400">/</span>
              <span className="font-medium text-slate-900">
                {folders.find(f => f.id === currentFolder)?.label}
              </span>
            </>
          )}
        </div>
        
        <div className="flex-1" />
        
        {/* Search - only in folder view */}
        {currentFolder && (
          <div className="relative w-52">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 text-sm"
              data-testid="search-input"
            />
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-auto p-4 bg-white">
        {/* Folder View - Root level */}
        {!currentFolder && (
          <div 
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
            data-testid="folders-grid"
          >
            {folders.map((folder) => {
              const count = getFolderCount(folder.id);
              const FolderIcon = folder.icon;
              
              return (
                <button
                  key={folder.id}
                  onClick={() => setCurrentFolder(folder.id)}
                  className={`
                    flex flex-col items-center justify-center p-6 rounded-xl
                    border-2 border-transparent
                    ${folder.bgColor} hover:border-slate-300
                    transition-all duration-200 group
                    focus:outline-none focus:ring-2 focus:ring-indigo-500/50
                  `}
                  data-testid={`folder-${folder.id}`}
                >
                  <div className="relative">
                    <Folder className={`w-16 h-16 ${folder.color} fill-current opacity-90 group-hover:scale-105 transition-transform`} />
                    <FolderIcon className="absolute bottom-2 right-1 w-5 h-5 text-white bg-slate-700/80 rounded p-0.5" />
                  </div>
                  <span className="mt-3 text-sm font-semibold text-slate-800">
                    {folder.label}
                  </span>
                  <span className="mt-1 text-xs text-slate-500">
                    {count} fichier{count !== 1 ? 's' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Files Grid - Inside a folder */}
        {currentFolder && (
          <>
            {filteredDocuments.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 py-16">
                <FolderOpen className="w-20 h-20 mb-3 opacity-40" />
                <p className="text-base">
                  {searchQuery ? 'Aucun resultat pour cette recherche' : 'Ce dossier est vide'}
                </p>
              </div>
            ) : (
              <div 
                className="grid gap-2"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}
                data-testid="files-grid"
              >
                {filteredDocuments.map((doc) => {
                  const { icon: FileIcon, color } = getFileIcon(doc.file_type);
                  const isSelected = selectedDoc === doc.document_id;
                  const isDownloading = downloading === doc.document_id;
                  
                  return (
                    <button
                      key={doc.document_id}
                      onClick={() => setSelectedDoc(doc.document_id)}
                      onDoubleClick={() => !isDownloading && downloadDocument(doc.document_id, doc.name, doc.file_type)}
                      disabled={isDownloading}
                      className={`
                        flex flex-col items-center p-3 rounded-lg transition-all
                        focus:outline-none
                        ${isDownloading ? 'opacity-70 cursor-wait' : ''}
                        ${isSelected 
                          ? 'bg-indigo-100 ring-2 ring-indigo-500' 
                          : 'hover:bg-slate-100'
                        }
                      `}
                      data-testid={`doc-${doc.document_id}`}
                      title={isDownloading ? 'Telechargement en cours...' : `${doc.name}.${doc.file_type} - Double-clic pour telecharger`}
                    >
                      <div className="relative">
                        {isDownloading ? (
                          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
                        ) : (
                          <FileIcon className={`w-12 h-12 ${isSelected ? 'text-indigo-600' : color}`} />
                        )}
                        <span 
                          className={`
                            absolute -bottom-1 -right-1 
                            text-[9px] font-bold uppercase 
                            px-1 py-0.5 rounded
                            ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'}
                          `}
                        >
                          {doc.file_type}
                        </span>
                      </div>
                      <span 
                        className={`
                          mt-2 text-xs text-center leading-tight
                          line-clamp-2 w-full
                          ${isSelected ? 'text-indigo-900 font-medium' : 'text-slate-700'}
                        `}
                      >
                        {doc.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
        <span>
          {currentFolder 
            ? `${filteredDocuments.length} fichier(s)`
            : `${folders.length} dossiers - ${documents.length} fichiers au total`
          }
        </span>
        {currentFolder && (
          <span>
            {downloading ? (
              <span className="flex items-center gap-1 text-indigo-600 font-medium">
                <Loader2 className="w-3 h-3 animate-spin" />
                Telechargement en cours...
              </span>
            ) : (
              'Double-cliquez pour telecharger'
            )}
          </span>
        )}
      </div>
    </div>
  );
};
